import { useCallback, useState, type FC, type SVGProps } from "react"

import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/16/solid"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom"

import useSolarProposalFinancing from "~/api/hooks/useSolarProposalFinancing"
import useSolarProposalSystemPricing from "~/api/hooks/useSolarProposalSystemPricing"
import ContractSigning from "~/components/Icons/ContractSigning"
import Financing from "~/components/Icons/Financing"
import HomeAndUtilityInfo from "~/components/Icons/HomeAndUtilityInfo"
import Pricing from "~/components/Icons/Pricing"
import Share from "~/components/Icons/Share"
import SystemPlacement from "~/components/Icons/SystemPlacement"

import {
  SolarExportPdfActionRow,
  useSolarExportPdfAction,
} from "./share/SolarExportPdfAction"
import {
  isSolarSetupMenuOpen,
  isSolarSharePath,
  SOLAR_SETUP_GROUPS,
  visibleSolarSetupParts,
  solarActiveSetupGroupFromPath,
  solarProposalPath,
  solarSetupGroupForStep,
  solarSetupGroupPath,
  solarSetupMenuPath,
  solarSharePath,
  solarSetupStepFromPath,
  solarStepFillsViewport,
  solarSetupStepPath,
  type SolarSetupGroupId,
} from "./solar-setup-paths"
import { powurProposalsUrl } from "./solar-urls"
import { PINNED_BATTERY_PROPOSAL_THEME } from "../BatteryOnlyProposal/theme/battery-proposal-theme"
import { useDesktopViewport } from "../BatteryOnlyProposal/use-desktop-viewport"
import { PRICING_OUT_OF_DATE_LABEL } from "../ProposalPresentation/pricing/pricing-out-of-date"
import { PRICING_REQUIRED_HINT } from "../ProposalSetup/pricing/pricing-copy"
import {
  GROUP_PARTS_LIST,
  PART_LINK_ACTIVE,
  PART_LINK_BASE,
  PART_LINK_INACTIVE,
  SETUP_MENU_WIDTH,
  STEP_LINK_ACTIVE,
  STEP_LINK_BASE,
  STEP_LINK_INACTIVE,
} from "../ProposalSetup/proposal-setup-rail"
import { matchProposalSetupSearch } from "../ProposalSetup/proposal-setup-search"
import {
  proposalSetupEscapeCommand,
  type ProposalSetupEscapeEvent,
} from "../ProposalSetup/proposal-setup-shortcuts"
import { ProposalSetupAccountLinks } from "../ProposalSetup/ProposalSetupAccountLinks"
import {
  ProposalSetupQuickSearch,
  type ProposalSetupSearchOption,
} from "../ProposalSetup/ProposalSetupQuickSearch"
import { useProposalSetupShortcuts } from "../ProposalSetup/use-proposal-setup-shortcuts"

const GROUP_ICONS: Record<SolarSetupGroupId, FC<SVGProps<SVGSVGElement>>> = {
  [`home-and-utility-info`]: HomeAndUtilityInfo,
  [`product-offering`]: Financing,
  [`system-placement`]: SystemPlacement,
  pricing: Pricing,
  contract: ContractSigning,
  share: Share,
}

/** The group whose sub-list holds Financing Status, which gates its financing read. */
const FINANCING_STATUS_GROUP_ID = solarSetupGroupForStep(`financing-status`).id

type SolarSetupSearchEntry = ProposalSetupSearchOption & {
  /** Where the entry goes; null runs Export To PDF in place. */
  readonly to: string | null
}

/** One palette entry per part: a step or Share opens its drawer, Export To
 * PDF runs. Groups have none. A step that waits for stored pricing explains
 * itself and cannot open. */
const solarSetupSearchEntries = (
  proposalId: string,
  hasPricing: boolean,
  exportLockedHint: string | null,
  loanProductRequired: boolean,
): readonly SolarSetupSearchEntry[] =>
  SOLAR_SETUP_GROUPS.flatMap((group) =>
    visibleSolarSetupParts(group, { loanProductRequired }).map((part) => ({
      id: part.id,
      label: part.label,
      groupLabel: group.label,
      lockedHint:
        part.kind === `action`
          ? exportLockedHint
          : part.kind === `step` && part.requiresPricing && !hasPricing
            ? PRICING_REQUIRED_HINT
            : null,
      to:
        part.kind === `step`
          ? solarSetupStepPath(proposalId, part.id)
          : part.kind === `route`
            ? solarSharePath(proposalId)
            : null,
    })),
  )

const PART_DISABLED = `flex flex-col gap-0.5 text-[0.875rem] leading-[21px] font-display p-[7px] mx-[5px] text-bp-text-dim cursor-not-allowed`

/**
 * The Solar Setup rail, opened at `/solar-proposal/:id/setup`, on a group at
 * `/setup/:groupId`, or beside a step's drawer, plus its Escape and ⌘K
 * shortcuts. Width, layer, theme and group styling match the Battery rail. It
 * reads System Pricing, financing and share readiness only while the group or
 * palette that shows them is open. The shell mounts it only for an actor who
 * may edit the proposal.
 */
export const SolarSetupMenu = ({
  hasPricing,
  powurProjectId,
  proposalId,
}: {
  /** Stored System Pricing exists; Incentives waits for it. */
  hasPricing: boolean
  powurProjectId: number | null
  proposalId: string
}): JSX.Element | null => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isDesktop = useDesktopViewport()
  const proposalPath = solarProposalPath(proposalId)
  const setupMenuPath = solarSetupMenuPath(proposalId)
  const isOpen = isSolarSetupMenuOpen(pathname, proposalId)
  const step = solarSetupStepFromPath(pathname, proposalId)
  const shareOpen = isSolarSharePath(pathname, proposalId)
  const activeGroupId = solarActiveSetupGroupFromPath(pathname, proposalId)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState(``)
  // The freshness hint sits under System Pricing, which only renders while the
  // Pricing group is expanded, so the status is read just for that case, as
  // Battery's rail does.
  const systemPricingQuery = useSolarProposalSystemPricing(
    isOpen && activeGroupId === `pricing` ? proposalId : undefined,
  )
  const pricingOutOfDate =
    systemPricingQuery.data?.pricingStatus === `out_of_date`
  // The Financing Status step is listed only for a plan that takes a Loan Product, so the
  // financing read is fetched while its group or the palette is on screen.
  const financingQuery = useSolarProposalFinancing(proposalId, {
    enabled:
      (isOpen && activeGroupId === FINANCING_STATUS_GROUP_ID) || searchOpen,
  })
  const loanProductRequired = financingQuery.data?.loanProductRequired === true
  // Readiness is read while Export To PDF is on screen or searchable.
  const exportAction = useSolarExportPdfAction(
    proposalId,
    (isOpen && activeGroupId === `share`) || searchOpen,
  )

  const setSearch = useCallback((open: boolean): void => {
    if (open) {
      setSearchQuery(``)
    }
    setSearchOpen(open)
  }, [])
  const escapeCommand = useCallback(
    (event: ProposalSetupEscapeEvent) =>
      proposalSetupEscapeCommand({
        ...event,
        // A step's or the Share drawer closes itself.
        drawerOwnsEscape: step != null || shareOpen,
        pathname,
        proposalPath,
        setupMenuOpen: isOpen,
        setupMenuPath,
      }),
    [isOpen, pathname, proposalPath, setupMenuPath, shareOpen, step],
  )
  useProposalSetupShortcuts({
    enabled: true,
    escapeCommand,
    searchOpen,
    setSearchOpen: setSearch,
  })

  if (typeof document === `undefined`) {
    return null
  }

  const searchEntries = solarSetupSearchEntries(
    proposalId,
    hasPricing,
    exportAction.lockedHint,
    loanProductRequired,
  )
  const search = searchOpen ? (
    <ProposalSetupQuickSearch
      alert={null}
      emptyLabel={
        searchEntries.length === 0
          ? `No setup steps yet`
          : `No matching setup steps`
      }
      matches={matchProposalSetupSearch(searchQuery, searchEntries)}
      onActivate={(entry) => {
        if (entry.lockedHint != null) return
        if (entry.to == null) {
          if (exportAction.start() === `started`) setSearchOpen(false)
          return
        }
        navigate(entry.to)
        setSearchOpen(false)
      }}
      onClose={() => setSearchOpen(false)}
      query={searchQuery}
      setQuery={setSearchQuery}
      testId="solar-setup-quick-search"
    />
  ) : null

  // Below lg a step drawer spans the viewport over the rail's layer, so the
  // rail yields until it closes, as Battery's does; Green Button Data's
  // full-width drawer does at every width. Search stays available.
  if (
    (step != null && (!isDesktop || solarStepFillsViewport(step))) ||
    (shareOpen && !isDesktop)
  ) {
    return search
  }

  return createPortal(
    <div
      className="pointer-events-none fixed inset-y-0 left-0 z-[2147483000] w-0 print:hidden"
      data-battery-theme={PINNED_BATTERY_PROPOSAL_THEME}
    >
      {search}
      {isOpen ? (
        <>
          {/* A step or the Share drawer brings its own backdrop, which closes
              it back to its group. */}
          {step == null && !shareOpen ? (
            <Link
              to={proposalPath}
              aria-label="Dismiss setup"
              className="pointer-events-auto fixed inset-0"
            />
          ) : null}
          <NavLink
            data-testid="solar-setup-menu-button"
            to={proposalPath}
            aria-label="Close setup"
            className="pointer-events-auto absolute top-[30px] left-[10px] z-[1000] flex h-[35px] w-[35px] items-center justify-center rounded-[5px] bg-transparent p-[5px] text-bp-text no-underline"
          >
            <XMarkIcon className="h-10 w-10" />
          </NavLink>
          <nav
            aria-label="Setup"
            className={`pointer-events-auto fixed top-0 left-0 flex h-full ${SETUP_MENU_WIDTH} flex-col overflow-y-auto border-r border-bp-border-strong bg-bp-elevated p-5 [&>*]:shrink-0`}
          >
            <h1 className="font-display pt-[18px] pb-6 text-center text-xl font-semibold text-bp-text capitalize">
              Setup
            </h1>
            <div className="mx-auto mb-5 h-px w-[95%] bg-bp-border-strong" />
            <ol className="flex w-full list-none flex-col">
              {SOLAR_SETUP_GROUPS.map((group) => {
                const Icon = GROUP_ICONS[group.id]
                const isActive = group.id === activeGroupId
                return (
                  <li key={group.id} className="flex flex-col">
                    <Link
                      to={solarSetupGroupPath(proposalId, group.id)}
                      // Beside a step's drawer the step link is the page; its
                      // group is the current item of the rail.
                      aria-current={
                        isActive ? (step == null ? `page` : `true`) : undefined
                      }
                      className={`${STEP_LINK_BASE} ${isActive ? STEP_LINK_ACTIVE : STEP_LINK_INACTIVE}`}
                    >
                      <Icon />
                      {group.label}
                    </Link>
                    {isActive ? (
                      <ol
                        aria-label={`${group.label} steps`}
                        className={GROUP_PARTS_LIST}
                      >
                        {visibleSolarSetupParts(group, {
                          loanProductRequired,
                        }).map((part) => (
                          <li key={part.id} className="flex flex-col">
                            {part.kind === `action` ? (
                              <SolarExportPdfActionRow
                                action={exportAction}
                                proposalId={proposalId}
                              />
                            ) : part.kind === `route` ? (
                              <NavLink
                                to={solarSharePath(proposalId)}
                                data-testid={`solar-setup-part-${part.id}`}
                                className={() =>
                                  `${PART_LINK_BASE} ${shareOpen ? PART_LINK_ACTIVE : PART_LINK_INACTIVE}`
                                }
                              >
                                {part.label}
                              </NavLink>
                            ) : part.requiresPricing && !hasPricing ? (
                              <span
                                aria-disabled="true"
                                data-testid={`solar-setup-part-${part.id}-disabled`}
                                className={PART_DISABLED}
                              >
                                {part.label}
                                <span className="text-[0.75rem] leading-tight text-bp-text-muted">
                                  {PRICING_REQUIRED_HINT}
                                </span>
                              </span>
                            ) : (
                              <NavLink
                                to={solarSetupStepPath(proposalId, part.id)}
                                // The Contract drawer closes to its launch
                                // point; from the rail that is this group.
                                state={
                                  part.id === `contract`
                                    ? {
                                        returnTo: solarSetupGroupPath(
                                          proposalId,
                                          group.id,
                                        ),
                                      }
                                    : undefined
                                }
                                className={({ isActive: partActive }) =>
                                  `${PART_LINK_BASE} ${partActive ? PART_LINK_ACTIVE : PART_LINK_INACTIVE}`
                                }
                              >
                                {part.id === `system-pricing` &&
                                pricingOutOfDate ? (
                                  <span className="flex flex-col gap-0.5">
                                    <span>{part.label}</span>
                                    <span
                                      data-testid="solar-setup-pricing-out-of-date"
                                      className="text-[0.75rem] leading-tight text-bp-gold"
                                    >
                                      {PRICING_OUT_OF_DATE_LABEL}
                                    </span>
                                  </span>
                                ) : (
                                  part.label
                                )}
                              </NavLink>
                            )}
                          </li>
                        ))}
                      </ol>
                    ) : null}
                  </li>
                )
              })}
            </ol>
            <div className="mx-auto my-5 h-px w-[95%] bg-bp-border-strong" />
            <ProposalSetupAccountLinks />
            {/* Leaves Vision, so a plain same-tab anchor, not a router link. */}
            <a
              href={powurProposalsUrl(powurProjectId)}
              className={`${STEP_LINK_BASE} ${STEP_LINK_INACTIVE}`}
            >
              <ArrowLeftIcon className="h-4 w-4" />
              {t(`backToPowur`)}
            </a>
          </nav>
        </>
      ) : null}
    </div>,
    document.body,
  )
}
